import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";

export const USER_COWORKER_QUERY_MAXIMUM_LENGTH = 200;

export class UserCoworkerFilterParameters {
  public readonly name?: string;
  public readonly email?: string;
  public readonly entityId?: Guid;

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.name) searchParams.append("name", this.name);
    if (this.email) searchParams.append("email", this.email);
    if (this.entityId) searchParams.append("entityId", this.entityId.value);
    return searchParams;
  }

  public constructor(name?: string, email?: string, entityId?: Guid) {
    validateOptionalText(name, "name", 200);
    validateOptionalText(email, "email", 320);
    if (entityId && (!(entityId instanceof Guid) || entityId.isEmpty)) {
      throw new Error("A valid entity identifier is required.");
    }

    this.name = name;
    this.email = email;
    this.entityId = entityId;
  }

  public isEqualTo(other: UserCoworkerFilterParameters): boolean {
    if (!other) return false;
    return this.name === other.name && this.email === other.email && this.entityId?.value === other.entityId?.value;
  }
}

export enum UserCoworkerOrderField {
  Name = "Name",
  Email = "Email",
  EntityName = "EntityName",
}

export class UserCoworkerOrderParameters {
  public readonly orderBy?: UserCoworkerOrderField;
  public readonly orderDir?: OrderDirection;

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (this.orderBy) params.append("orderBy", this.orderBy);
    if (this.orderDir) params.append("orderDir", this.orderDir);
    return params;
  }

  public constructor(orderBy?: UserCoworkerOrderField, orderDir?: OrderDirection) {
    if (orderBy && !Object.values(UserCoworkerOrderField).includes(orderBy)) {
      throw new Error("A valid coworker order field is required.");
    }
    if (orderDir && !Object.values(OrderDirection).includes(orderDir)) {
      throw new Error("A valid order direction is required.");
    }

    this.orderBy = orderBy;
    this.orderDir = orderDir;
  }

  public isEqualTo(other: UserCoworkerOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }
}

export default class UserCoworkerQuery {
  public readonly filterParameters: UserCoworkerFilterParameters;
  public readonly orderParameters: UserCoworkerOrderParameters;
  public readonly paginationParameters: PaginationParameters;
  public readonly query?: string;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.paginationParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.filterParameters.searchParams,
      ...(this.query ? [["query", this.query]] : []),
    ]);
  }

  public get cacheKey(): string {
    return new URLSearchParams([
      ...this.orderParameters.searchParams,
      ...this.filterParameters.searchParams,
      ...(this.query ? [["query", this.query]] : []),
    ]).toString();
  }

  public constructor(
    filterParameters: UserCoworkerFilterParameters,
    orderParameters: UserCoworkerOrderParameters,
    paginationParameters: PaginationParameters,
    query?: string
  ) {
    if (!(paginationParameters instanceof PaginationParameters) ||
        !Number.isInteger(paginationParameters.pageIndex) || paginationParameters.pageIndex < 0 ||
        !Number.isInteger(paginationParameters.pageSize) || paginationParameters.pageSize < 1 ||
        paginationParameters.pageSize > 100) {
      throw new Error("Valid pagination parameters are required.");
    }
    validateOptionalText(query, "query", USER_COWORKER_QUERY_MAXIMUM_LENGTH);

    this.paginationParameters = paginationParameters;
    this.orderParameters = orderParameters;
    this.filterParameters = filterParameters;
    this.query = query;
  }

  public withPagination(paginationParameters: PaginationParameters): UserCoworkerQuery {
    return new UserCoworkerQuery(
      this.filterParameters,
      this.orderParameters,
      paginationParameters,
      this.query,
    );
  }

  public isEqualTo(other: UserCoworkerQuery): boolean {
    if (!other) {
      return false;
    }
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters) &&
      this.query === other.query
    );
  }
}

function validateOptionalText(value: string | undefined, field: string, maximumLength: number): void {
  if (value !== undefined && (typeof value !== "string" || value.length > maximumLength)) {
    throw new Error(`${field} cannot exceed ${maximumLength} characters.`);
  }
}
